import { useCallback, useMemo, useState } from 'react';
import { useSupercluster } from '@/hooks/use-supercluster';
import { Button } from '@/shared/components/ui/button';
import { CircularProgress, Drawer } from '@mui/material';
import {
  AdvancedMarker,
  Map,
  type MapCameraChangedEvent,
  type MapProps,
} from '@vis.gl/react-google-maps';
import ChargePointDetails from 'components/ChargePointDetails';
import { useAppDispatch, useAppSelector } from 'features';
import {
  getSelectedChargePointSelector,
  setSelectedChargePoint,
  unselectChargePoint,
} from 'features/ChargePoints';
import { selectIsFetchingWhiteLabels, selectWhiteLabelConfig } from 'features/Company';
import { unselectConnector } from 'features/Connectors';
import { X } from 'lucide-react';
import { shallowEqual } from 'react-redux';
import { type AnyProps } from 'supercluster';
import type Supercluster from 'supercluster';
import { type ChargePoint, type Connector, type LatLng } from 'types/swagger';
import {
  isChargePointNotAvailable,
  isConnectorLatestStatusAvailable,
  isConnectorNotAvailable,
} from 'util/connector-status';
import { metersToLatLng, rotatePoint } from 'util/marker-positioning';

import CustomMarker from './CustomMarker';
import Legend from './Legend';

type ChargePointWithLocation = ChargePoint & { connectors: number[]; location: LatLng };

export interface MapDetails {
  center: google.maps.LatLngLiteral;
  bounds: google.maps.LatLngBoundsLiteral;
  zoom: number;
  heading: number;
  tilt: number;
}

const ChargePointMap = () => {
  const dispatch = useAppDispatch();

  const whitelabelConfig = useAppSelector(selectWhiteLabelConfig);
  const isFetchingWhiteLabelConfig = useAppSelector(selectIsFetchingWhiteLabels);
  const areChargePointsLoading = useAppSelector((state) => state.chargePoints.isFetching);

  const chargePoints: Record<number, ChargePoint> = useAppSelector(
    (state) => state.chargePoints.byId
  );

  const connectors: Record<number, Connector> = useAppSelector((state) => state.connectors.byId);
  const selectedChargePoint = useAppSelector(getSelectedChargePointSelector, shallowEqual);

  const [, setOrigins] = useState<google.maps.Circle[]>([]);
  const [, setLinesToOrigin] = useState<google.maps.Polyline[]>([]);
  const [cameraChangeEvent, setCameraChangeEvent] = useState<MapCameraChangedEvent | null>(null);
  const initialCoords = useMemo(
    () => ({
      lat: parseFloat(whitelabelConfig?.default_latitude) || -33.4489,
      lng: parseFloat(whitelabelConfig?.default_longitude) || -70.6693,
    }),
    [whitelabelConfig]
  );

  const declutteredChargePoints = useMemo(() => {
    const chargePointsByLocation: Record<string, ChargePointWithLocation[]> = {};
    const separatedChargePoints: ChargePointWithLocation[] = [];

    Object.values(chargePoints).forEach((cp) => {
      if (!cp.location) return;
      const cpWithLocation = cp as ChargePointWithLocation;
      const key = `${cpWithLocation.location.latitude}-${cpWithLocation.location.longitude}`;
      if (!chargePointsByLocation[key]) chargePointsByLocation[key] = [];
      chargePointsByLocation[key].push(cpWithLocation);
    });
    const _origins: google.maps.Circle[] = [];
    const _linesToOrigin: google.maps.Polyline[] = [];
    Object.values(chargePointsByLocation).forEach((cps) => {
      if (cps.length === 1) return separatedChargePoints.push(cps[0]);

      const distance = 5;
      const angle = 360 / cps.length;

      cps.forEach((cp, index) => {
        if (!cp.location) return;
        const [dX, dY] = rotatePoint(0, 0, 0, distance, angle * index);
        const [lat, lng] = metersToLatLng(cp.location.latitude, cp.location.longitude, dX, dY);
        const pathToOrigin = [
          { lat: cp.location.latitude, lng: cp.location.longitude },
          { lat, lng },
        ];
        if (index === 0) {
          const origin = new google.maps.Circle({
            strokeColor: '#FF0000',
            strokeOpacity: 0.5,
            strokeWeight: 1,
            fillColor: '#FF0000',
            fillOpacity: 0.35,
            center: { lat: cp.location.latitude, lng: cp.location.longitude },
            radius: 0.25,
          });
          _origins.push(origin);
        }
        const lineToOrigin = new google.maps.Polyline({
          path: pathToOrigin,
          geodesic: true,
          strokeColor: '#FF0000',
          strokeOpacity: 0.5,
          strokeWeight: 1,
        });
        _linesToOrigin.push(lineToOrigin);
        separatedChargePoints.push({
          ...cp,
          location: { latitude: lat, longitude: lng },
        });
      });
    });
    setOrigins(_origins);
    setLinesToOrigin(_linesToOrigin);

    return separatedChargePoints;
  }, [chargePoints]);

  const points = useMemo(
    () => ({
      type: 'FeatureCollection' as const,
      features: declutteredChargePoints.map((cp) => {
        return {
          id: cp.id,
          type: 'Feature' as const,
          geometry: {
            type: 'Point' as const,
            coordinates: [cp.location.longitude, cp.location.latitude],
          },
          properties: { chargePointId: cp.id, cluster: false },
        };
      }),
    }),
    [declutteredChargePoints]
  );

  const superclusterOptions = useMemo(
    () => ({
      radius: 45,
      maxZoom: 20,
    }),
    []
  );

  const { clusters, getLeaves, getClusterExpansionZoom } = useSupercluster(
    points,
    'chargepoint-map',
    superclusterOptions
  );

  const selectedChargePointConnectors = useMemo(() => {
    return (selectedChargePoint?.connectors as unknown as number[])?.map(
      (connectorId) => connectors[connectorId]
    );
  }, [connectors, selectedChargePoint]);

  const handleCloseDrawer = useCallback(() => {
    dispatch(unselectConnector());
    dispatch(unselectChargePoint());
  }, [dispatch]);

  const getChargePointConnectors = useCallback(
    (cp: ChargePoint) => {
      return cp?.connectors?.length
        ? (cp.connectors as unknown as number[])
            .map((connectorId) => connectors[connectorId])
            .filter((connector) => connector?.connector_id !== 0)
        : [];
    },
    [connectors]
  );

  const onChargePointClick = useCallback(
    (chargePoint: ChargePoint) => {
      dispatch(setSelectedChargePoint(chargePoint));
    },
    [dispatch]
  );

  const onClusterClick = useCallback(
    (cluster: (typeof clusters)[0]) => {
      const map = cameraChangeEvent?.map;
      map?.panTo({
        lng: cluster.geometry.coordinates[0],
        lat: cluster.geometry.coordinates[1],
      });
      map?.setZoom(getClusterExpansionZoom(Number(cluster.id) || 0));
    },
    [getClusterExpansionZoom, cameraChangeEvent]
  );

  const renderChargePoint = useCallback(
    (chargePoint: ChargePoint) => {
      if (!chargePoint?.location) return null;

      const clusterConnectors: {
        available: Connector[];
        occupied: Connector[];
        unavailable: Connector[];
      } = {
        available: [],
        occupied: [],
        unavailable: [],
      };

      const chargePointConnectors = getChargePointConnectors(chargePoint);

      isChargePointNotAvailable(chargePoint)
        ? clusterConnectors.unavailable.push(...chargePointConnectors)
        : chargePointConnectors.forEach((connector) => {
            isConnectorNotAvailable(connector)
              ? clusterConnectors.unavailable.push(connector)
              : isConnectorLatestStatusAvailable(connector)
                ? clusterConnectors.available.push(connector)
                : clusterConnectors.occupied.push(connector);
          });

      const sortedConnectors = [
        ...clusterConnectors.available,
        ...clusterConnectors.occupied,
        ...clusterConnectors.unavailable,
      ];
      return (
        <CustomMarker sortedConnectors={sortedConnectors} clusterConnectors={clusterConnectors} />
      );
    },
    [getChargePointConnectors]
  );

  const renderCluster = useCallback(
    (cluster: Supercluster.ClusterFeature<Supercluster.AnyProps>) => {
      const clusterChargePoints: ChargePoint[] = [];
      const clusterConnectors: {
        available: Connector[];
        occupied: Connector[];
        unavailable: Connector[];
      } = {
        available: [],
        occupied: [],
        unavailable: [],
      };
      const leaves = getLeaves(cluster.properties.cluster_id);
      const chargePointsIdsInCluster = leaves
        .filter((p) => Boolean(p.properties.chargePointId))
        .map((p) => p.properties.chargePointId);
      chargePointsIdsInCluster.forEach((cpId) => {
        const chargePoint = chargePoints[cpId];
        if (!chargePoint) return null;

        clusterChargePoints.push(chargePoint);

        const chargePointConnectors = getChargePointConnectors(chargePoint);

        isChargePointNotAvailable(chargePoint)
          ? clusterConnectors.unavailable.push(...chargePointConnectors)
          : chargePointConnectors.forEach((connector) => {
              isConnectorNotAvailable(connector)
                ? clusterConnectors.unavailable.push(connector)
                : isConnectorLatestStatusAvailable(connector)
                  ? clusterConnectors.available.push(connector)
                  : clusterConnectors.occupied.push(connector);
            });
      });

      const sortedConnectors = [
        ...clusterConnectors.available,
        ...clusterConnectors.occupied,
        ...clusterConnectors.unavailable,
      ];

      return (
        <CustomMarker sortedConnectors={sortedConnectors} clusterConnectors={clusterConnectors} />
      );
    },
    [getLeaves, chargePoints, getChargePointConnectors]
  );

  const onMapCameraChanged = useCallback((e: MapCameraChangedEvent) => {
    setCameraChangeEvent(e);
    setLinesToOrigin((prev) =>
      prev.map((line) => {
        line.setMap(e.detail.zoom >= 20 ? e.map : null);
        return line;
      })
    );
    setOrigins((prev) =>
      prev.map((origin) => {
        origin.setMap(e.detail.zoom >= 20 ? e.map : null);
        return origin;
      })
    );
  }, []);

  const mapOptions: MapProps = useMemo(() => {
    return {
      defaultZoom: Number(whitelabelConfig?.default_zoom) || 10,
      defaultCenter: initialCoords,
      mapId: 'chargepoint-map',
      clickableIcons: false,
      fullscreenControl: false,
      streetViewControl: false,
      mapTypeControl: false,
      renderingType: google.maps.RenderingType.RASTER,
      onCameraChanged: onMapCameraChanged,
    };
  }, [initialCoords, onMapCameraChanged, whitelabelConfig]);

  return isFetchingWhiteLabelConfig || areChargePointsLoading ? (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '250px',
        gap: '20px',
        marginTop: '20px',
      }}
    >
      <CircularProgress />
    </div>
  ) : (
    <>
      <div className="map-container">
        <Map {...mapOptions}>
          {clusters.map((c, index) => {
            return (
              <AdvancedMarker
                key={
                  c.properties.cluster
                    ? `cluster-${index}`
                    : `chargePoint-${c.properties.chargePointId}`
                }
                position={{ lng: c.geometry.coordinates[0], lat: c.geometry.coordinates[1] }}
                onClick={() =>
                  c.properties.cluster
                    ? onClusterClick(c)
                    : onChargePointClick(chargePoints[c.properties.chargePointId])
                }
              >
                {c.properties.cluster
                  ? renderCluster(c as Supercluster.ClusterFeature<AnyProps>)
                  : renderChargePoint(chargePoints[c.properties.chargePointId])}
              </AdvancedMarker>
            );
          })}
        </Map>
        <Legend />
      </div>
      <Drawer
        anchor="right"
        open={!!selectedChargePoint}
        onClose={handleCloseDrawer}
        PaperProps={{ className: 'charge-center-drawer' }}
      >
        <div className="d-flex items-align-center justify-content-end sticky-top bg-martinique-blue-dark">
          <Button size="icon" variant="ghost" onClick={handleCloseDrawer}>
            <X />
          </Button>
        </div>
        {selectedChargePoint ? (
          <ChargePointDetails
            chargePoint={selectedChargePoint}
            chargePointConnectors={selectedChargePointConnectors}
          />
        ) : null}
      </Drawer>
    </>
  );
};

export default ChargePointMap;
